import { useAuth } from '@clerk/clerk-react';
import { HintProvider, createHintsRemote, useQueuedTip, type Hint } from '@flobi/asset-library/hints';
import { useTourActive } from '@flobi/asset-library/tours';
// The tokens the card is drawn with (ArtworkNotifications brings them too), then the card.
import '@flobi/asset-library/share.css';
import '@flobi/asset-library/hints.css';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { currentTokenGetter } from '../../services/apiClient';
import { useTheme } from '../../theme';
import { AppDirectoryLogo } from '../../ui/AppDirectoryLogo';
import { useAppLogo } from '../appDirectory';
import { IS_MAC } from '../../ui/keys';
import { ARTWORK_HINTS } from './artworkHints';
import { listenForShortcutSignals } from './shortcutSignals';

/** Where "Learn more" goes: the Flobi Learn site. Unset, the button is left out. */
const LEARN_URL = (import.meta.env.VITE_FLOBI_LEARN_URL as string | undefined) || '';
const GATEWAY = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? '';

/**
 * flobi-hints-back through the gateway, quietly. Not `gatewayCall`: apiClient sends a 401 to
 * sign-in, and the hints service may not be deployed yet. A failed call here shows nothing; the
 * kit rests the remote and decides with ARTWORK_HINTS' own rules meanwhile.
 */
async function quietCall(service: string, action: string, data: Record<string, unknown>): Promise<unknown> {
  const token = await currentTokenGetter()?.().catch(() => null);
  const res = await fetch(`${GATEWAY}/${service}/${action}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(data),
    signal: AbortSignal.timeout(5000),
  });
  const body = (await res.json().catch(() => null)) as { success?: boolean; data?: unknown } | null;
  if (!res.ok || !body || body.success === false) throw new Error(`hints: ${action} failed`);
  return body.data;
}

const HINTS_REMOTE = createHintsRemote(quietCall, 'artwork');

/**
 * A shortcut pressed for the person, as the editor hears one: the tool keys are EditorPage's
 * (⇧C the Cloner, ⇧M the Shape Builder, K the Slice tool). Released at once, so nothing thinks
 * a modifier is held.
 */
function press(key: string, code: string, { shift = true, command = false, alt = false } = {}) {
  const mods = { shiftKey: shift, altKey: alt, metaKey: command && IS_MAC, ctrlKey: command && !IS_MAC };
  window.dispatchEvent(new KeyboardEvent('keydown', { key, code, ...mods, bubbles: true, cancelable: true }));
  window.dispatchEvent(new KeyboardEvent('keyup', { key, code, bubbles: true, cancelable: true }));
}

/**
 * A request EditorPage answers by cancelling the event once it has done it (Document settings,
 * Assets on a place). Unanswered — a viewer's file, the editor not open — the kit is told
 * `false`, so the card counts as closed, not learned.
 */
function ask(event: Event): false | undefined {
  return window.dispatchEvent(event) ? false : undefined;
}

/**
 * Properties brought out (EditorPage's ⌥⌘I, its own key for the inspector). That key toggles, so
 * it is pressed only while Properties is not out: a button that says Open never closes it.
 */
function openProperties() {
  if (document.querySelector('aside[aria-label="Properties"]')) return;
  press('i', 'KeyI', { shift: false, command: true, alt: true });
}

/**
 * The tip the Tips page's "Try it" asked for (`queueTip`), played once the file it opened is up:
 * a moment after the editor's route turns the card on, so it lands over the canvas, not the curtain.
 */
function PlayQueuedTip({ ready }: { ready: boolean }) {
  useQueuedTip({ ready, delay: 1800 });
  return null;
}

/**
 * Artwork's hints: the corner card from `@flobi/asset-library/hints`, mounted once in App and
 * switched on inside a file only. The card follows the editor's theme as it changes (`useTheme`
 * resolves System to the OS's light or dark and re-renders on a switch). Signals come from `emitHintSignal` calls in the quick bar,
 * the canvas menu, the Shape Builder, the Cloner, Export and `shortcutSignals`.
 */
export function ArtworkHints() {
  const { isSignedIn } = useAuth();
  const { theme } = useTheme();
  // The card's app mark: the App Directory's logo in the person's style on the editor's theme
  // (lib/appDirectory) — always an element, so with none the kit draws an empty box, never its letter.
  const logo = useAppLogo(theme === 'dark' ? 'dark' : 'light');
  // Every Artwork hint is about the editor, and its buttons press the editor's keys: on Home
  // they would show a tool that is not there (review 9 Oct). So only on a file, `/f/…`.
  const { pathname } = useLocation();
  // A running tour has the screen to itself.
  const touring = useTourActive();
  const enabled = Boolean(isSignedIn) && pathname.startsWith('/f/') && !touring;

  useEffect(() => (enabled ? listenForShortcutSignals() : undefined), [enabled]);

  const appIcon = useCallback((slug: string) => <AppDirectoryLogo src={logo(slug || 'artwork')} />, [logo]);

  const onAction = useCallback((hint: Hint) => {
    switch (hint.action?.id) {
      case 'tool-cloner':
        return press('C', 'KeyC');
      case 'tool-builder':
        return press('M', 'KeyM');
      case 'tool-slice':
        // A bare K: ⇧K is Measure.
        return press('k', 'KeyK', { shift: false });
      case 'open-properties':
        return openProperties();
      case 'open-export':
        // What ⇧⌘E and File ▸ Export send; the toolbar opens the panel on it.
        window.dispatchEvent(new Event('artwork:export'));
        return undefined;
      case 'document-settings':
        return ask(new Event('artwork:document-settings', { cancelable: true }));
      case 'open-brand':
        return ask(new CustomEvent('artwork:library', { detail: { place: 'brand' }, cancelable: true }));
      case 'open-my-stuff':
        return ask(new CustomEvent('artwork:library', { detail: { place: 'mystuff' }, cancelable: true }));
      default:
        return undefined;
    }
  }, []);

  const onLearnMore = useMemo(
    () => (LEARN_URL ? (_hint: Hint, _vars: unknown, url?: string | null) => url && void window.open(url, '_blank', 'noopener') : undefined),
    [],
  );

  return (
    <HintProvider
      app="artwork"
      theme="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      hints={ARTWORK_HINTS}
      remote={HINTS_REMOTE}
      enabled={enabled}
      appIcon={appIcon}
      appLabel={() => 'Artwork'}
      onAction={onAction}
      learnBase={LEARN_URL || undefined}
      onLearnMore={onLearnMore}
      debug={import.meta.env.DEV}
    >
      <PlayQueuedTip ready={enabled} />
    </HintProvider>
  );
}
